// src/pages/Usuarios.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  LockOpenIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';

const selectClassName =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// --- COMPONENTES REUTILIZÁVEIS ---
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
      onClick={onClose}
    >
      <Card
        className="relative w-full max-w-md max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3 z-10"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
        <CardContent className="pt-6 overflow-y-auto max-h-[90vh]">{children}</CardContent>
      </Card>
    </div>
  );
};

// Modal para gerenciamento de créditos
const CreditsModal = ({ user, onSave, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    quantidade: '',
    tipo: 'adicionar', // 'adicionar' ou 'remover'
    descricao: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantidade' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      userId: user._id,
      quantidade: formData.tipo === 'remover' ? -Math.abs(formData.quantidade) : Math.abs(formData.quantidade),
      descricao: formData.descricao || (formData.tipo === 'adicionar' ? 'Créditos adicionados pelo administrador' : 'Créditos removidos pelo administrador')
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Gerenciar Créditos</h2>
        <p className="text-sm text-muted-foreground">
          Usuário: <strong className="text-foreground">{user.nome || user.name || 'Sem nome'}</strong>
        </p>
        <p className="text-sm text-muted-foreground">
          Saldo atual: <strong className="text-foreground">{user.creditos?.saldo?.toFixed(2) || '0.00'}</strong> créditos
        </p>
      </div>
      <div>
        <Label htmlFor="tipo">Ação</Label>
        <select id="tipo" name="tipo" value={formData.tipo} onChange={handleChange} className={`${selectClassName} mt-1`}>
          <option value="adicionar">Adicionar Créditos</option>
          <option value="remover">Remover Créditos</option>
        </select>
      </div>
      <div>
        <Label htmlFor="quantidade">Quantidade</Label>
        <Input
          id="quantidade"
          name="quantidade"
          type="number"
          step="0.01"
          min="0"
          value={formData.quantidade}
          onChange={handleChange}
          className="mt-1"
          required
        />
      </div>
      <div>
        <Label htmlFor="descricao">Descrição (opcional)</Label>
        <Input
          id="descricao"
          name="descricao"
          type="text"
          value={formData.descricao}
          onChange={handleChange}
          className="mt-1"
          placeholder="Motivo da alteração"
        />
      </div>
      <div className="pt-4 flex justify-end gap-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Processando...' : 'Confirmar'}
        </Button>
      </div>
    </form>
  );
};

const CreditLogsModal = ({ user, logs = [], loading, onClose }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold">Histórico de Créditos</h2>
        <p className="text-sm text-muted-foreground">
          Usuário: <strong className="text-foreground">{user?.nome || user?.name || 'Sem nome'}</strong>
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando histórico...</p>
      ) : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada.</p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto border rounded-md">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2">Data</th>
                <th className="p-2">Tipo</th>
                <th className="p-2 text-right">Quantidade</th>
                <th className="p-2 text-right">Saldo Antes</th>
                <th className="p-2 text-right">Saldo Depois</th>
                <th className="p-2">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id} className="border-t border-border">
                  <td className="p-2 whitespace-nowrap">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString('pt-BR') : '-'}
                  </td>
                  <td className="p-2">{log.tipo || '-'}</td>
                  <td className="p-2 text-right font-mono">{Number(log.quantidade || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{Number(log.saldoAnterior || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{Number(log.saldoPosterior || 0).toFixed(2)}</td>
                  <td className="p-2">{log.descricao || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-2 flex justify-end">
        <Button type="button" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  );
};

const UserForm = ({ user, onSave, onCancel, isSubmitting, currentUserRole, assistentes = [], phoneNumbers = [] }) => {
  const [formData, setFormData] = useState(() => {
    // Inicializar com valores do usuário se estiver editando, senão valores padrão
    if (user) {
      return {
        nome: user.nome || '',
        email: user.email || '',
        senha: '',
        role: user.role || 'cliente',
        tipo: user.tipo || '',
        limiteAssistentes: user.limiteAssistentes || 1,
        taxa: user.taxa || 0,
        // Backend usa `vapiApiKey`. Mantemos fallback para `ApiKey` caso exista em dados antigos.
        vapiApiKey: user.vapiApiKey || user.ApiKey || '',
        orgID: user.orgID || '',
        followUpGlobalConfig: {
          habilitado: user.followUpGlobalConfig?.habilitado ?? false,
          maxTentativasTotal: user.followUpGlobalConfig?.maxTentativasTotal ?? 4,
          maxTentativasPorDia: user.followUpGlobalConfig?.maxTentativasPorDia ?? 1,
          intervaloMinutos: user.followUpGlobalConfig?.intervaloMinutos ?? 1440,
          assistenteFollowUpId: user.followUpGlobalConfig?.assistenteFollowUpId || '',
          phoneNumberFollowUpId: user.followUpGlobalConfig?.phoneNumberFollowUpId || ''
        },
        _id: user._id
      };
    }
    return {
      nome: '',
      email: '',
      senha: '',
      role: 'cliente',
      tipo: '',
      limiteAssistentes: 1,
      taxa: 0,
      vapiApiKey: '',
      orgID: '',
      followUpGlobalConfig: {
        habilitado: false,
        maxTentativasTotal: 4,
        maxTentativasPorDia: 1,
        intervaloMinutos: 1440,
        assistenteFollowUpId: '',
        phoneNumberFollowUpId: ''
      }
    };
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (name.startsWith('followUpGlobalConfig.')) {
      const field = name.split('.')[1];
      const fieldValue = field === 'habilitado' ? e.target.checked : value;
      setFormData(prev => ({
        ...prev,
        followUpGlobalConfig: {
          ...prev.followUpGlobalConfig,
          [field]: ['maxTentativasTotal', 'maxTentativasPorDia', 'intervaloMinutos'].includes(field)
            ? Number(fieldValue)
            : fieldValue
        }
      }));
      return;
    }
    if (name === 'taxa') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value, 10) : value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };



  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-semibold">{user ? 'Editar Usuário' : 'Criar Novo Usuário'}</h2>
      <div>
        <Label htmlFor="nome">Nome</Label>
        <Input id="nome" name="nome" type="text" value={formData.nome} onChange={handleChange} className="mt-1" required />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className="mt-1" required />
      </div>
      <div>
        <Label htmlFor="senha">Senha</Label>
        <Input
          id="senha"
          name="senha"
          type="password"
          onChange={handleChange}
          className="mt-1"
          required={!user}
          placeholder={user ? 'Deixe em branco para não alterar' : ''}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="role">Tipo</Label>
          <select id="role" name="role" value={formData.role} onChange={handleChange} className={`${selectClassName} mt-1`}>
            <option value="cliente">Cliente</option>
            {currentUserRole === 'super_admin' && (
              <option value="admin">Admin (cliente White Label)</option>
            )}
          </select>
        </div>
        <div>
          <Label htmlFor="limiteAssistentes">Limite de Assistentes</Label>
          <Input id="limiteAssistentes" name="limiteAssistentes" type="number" min="0" value={formData.limiteAssistentes} onChange={handleChange} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="taxa">Taxa por Chamada (Ex: 0.10)</Label>
          <Input id="taxa" name="taxa" type="number" step="0.01" min="0" value={formData.taxa} onChange={handleChange} className="mt-1" />
        </div>
        {user && (
          <div>
            <Label>Saldo de Créditos</Label>
            <Input
              type="text"
              value={`${user.creditos?.saldo?.toFixed(2) || '0.00'} créditos`}
              readOnly
              className="mt-1 cursor-not-allowed bg-muted"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="vapiApiKey">API Key/Label</Label>
          <Input
            id="vapiApiKey"
            name="vapiApiKey"
            type="text"
            value={formData.vapiApiKey}
            onChange={handleChange}
            className="mt-1"
            placeholder="Chave API"
          />
        </div>
        <div>
          <Label htmlFor="orgID">Org ID</Label>
          <Input
            id="orgID"
            name="orgID"
            type="text"
            value={formData.orgID}
            onChange={handleChange}
            className="mt-1"
            placeholder="ID da Organização"
          />
        </div>
      </div>
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            id="followUpGlobalConfig.habilitado"
            name="followUpGlobalConfig.habilitado"
            type="checkbox"
            checked={!!formData.followUpGlobalConfig?.habilitado}
            onChange={handleChange}
          />
          <Label htmlFor="followUpGlobalConfig.habilitado">Ativar Follow-up global</Label>
        </div>
        {formData.followUpGlobalConfig?.habilitado && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Máx total</Label>
                <Input name="followUpGlobalConfig.maxTentativasTotal" type="number" min="1" max="20" value={formData.followUpGlobalConfig?.maxTentativasTotal ?? 4} onChange={handleChange} className="mt-1" />
              </div>
              <div>
                <Label>Máx por dia</Label>
                <Input name="followUpGlobalConfig.maxTentativasPorDia" type="number" min="1" max="10" value={formData.followUpGlobalConfig?.maxTentativasPorDia ?? 1} onChange={handleChange} className="mt-1" />
              </div>
              <div>
                <Label>Intervalo (min)</Label>
                <Input name="followUpGlobalConfig.intervaloMinutos" type="number" min="1" max="10080" value={formData.followUpGlobalConfig?.intervaloMinutos ?? 1440} onChange={handleChange} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assistente do Follow-up</Label>
                <select name="followUpGlobalConfig.assistenteFollowUpId" value={formData.followUpGlobalConfig?.assistenteFollowUpId || ''} onChange={handleChange} className={`${selectClassName} mt-1`}>
                  <option value="">Usar assistente da chamada original</option>
                  {assistentes.map((a) => (
                    <option key={a.vapiId || a._id} value={a.vapiId || a._id}>{a.nome || a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Número do Follow-up</Label>
                <select name="followUpGlobalConfig.phoneNumberFollowUpId" value={formData.followUpGlobalConfig?.phoneNumberFollowUpId || ''} onChange={handleChange} className={`${selectClassName} mt-1`}>
                  <option value="">Usar número da chamada original</option>
                  {phoneNumbers.map((pn) => (
                    <option key={pn.vapiId || pn._id} value={pn.vapiId || pn._id}>{pn.name} ({pn.number})</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="pt-4 flex justify-end gap-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancelar</Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
};

// --- COMPONENTE PRINCIPAL ---
export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [creditLogs, setCreditLogs] = useState([]);
  const [loadingCreditLogs, setLoadingCreditLogs] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
  const [selectedUserForLogs, setSelectedUserForLogs] = useState(null);
  const [assistentes, setAssistentes] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
  const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
  const currentUserRole = storedUserData.role || localStorage.getItem('userRole') || 'cliente';
  const isAdminOrSuper = currentUserRole === 'admin' || currentUserRole === 'super_admin';
  const isSuperAdmin = currentUserRole === 'super_admin';

  // --- FUNÇÃO HELPER PARA CONFIGURAR HEADERS DE AUTENTICAÇÃO ---
  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      return null;
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const handleOpenCreditLogs = async (user) => {
    if (!isSuperAdmin) return;
    const config = getAuthConfig();
    if (!config) return;

    setSelectedUserForLogs(user);
    setIsLogsModalOpen(true);
    setLoadingCreditLogs(true);
    setCreditLogs([]);

    try {
      const response = await axios.get(`${baseUrl}/api/usuarios/${user._id}/creditos/historico`, config);
      setCreditLogs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao carregar histórico de créditos');
      setCreditLogs([]);
    } finally {
      setLoadingCreditLogs(false);
    }
  };

  // Função para bloquear/desbloquear usuário
  const handleToggleBlockUser = async (user) => {
    if (window.confirm(`Tem certeza que deseja ${user.creditos?.bloqueado ? 'desbloquear' : 'bloquear'} o usuário "${user.nome || user.name || 'Sem nome'}"?`)) {
      const config = getAuthConfig();
      if (!config) return;

      const toastId = toast.loading(user.creditos?.bloqueado ? 'Desbloqueando...' : 'Bloqueando...');

      try {
        // Atualiza apenas o status de bloqueio
        const response = await axios.put(
          `${baseUrl}/api/usuarios/${user._id}`,
          {
            creditos: {
              bloqueado: !user.creditos?.bloqueado
            }
          },
          config
        );

        toast.success(
          user.creditos?.bloqueado ? 'Usuário desbloqueado com sucesso!' : 'Usuário bloqueado com sucesso!',
          { id: toastId }
        );
        fetchUsers(); // Atualiza a lista de usuários
      } catch (err) {
        toast.error('Erro ao atualizar status de bloqueio.', { id: toastId });
        console.error('Erro ao atualizar status de bloqueio:', err);
      }
    }
  };

  // Função para buscar usuários
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const config = getAuthConfig();
    if (!config) {
      console.log('Nenhum token encontrado, redirecionando para login...');
      return setLoading(false);
    }

    try {
      const [usersRes, assistentesRes, phoneNumbersRes] = await Promise.all([
        axios.get(`${baseUrl}/api/usuarios`, config),
        axios.get(`${baseUrl}/api/assistentes/master`, config).catch(() => ({ data: [] })),
        axios.get(`${baseUrl}/api/phoneNumbers`, config).catch(() => ({ data: [] }))
      ]);
      setUsuarios(usersRes.data);
      setAssistentes(Array.isArray(assistentesRes.data) ? assistentesRes.data : []);
      setPhoneNumbers(Array.isArray(phoneNumbersRes.data) ? phoneNumbersRes.data : []);
      setError('');
    } catch (err) {
      setError('Erro ao listar usuários.');
      console.error('Erro ao buscar usuários:', err.response ? err.response.data : err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Função para salvar usuário
  const handleSaveUser = async (formData) => {
    setIsSubmitting(true);
    const config = getAuthConfig();
    if (!config) return setIsSubmitting(false);

    const toastId = toast.loading(formData._id ? 'Atualizando...' : 'Criando...');
    const isEditing = !!formData._id;
    const url = isEditing ? `${baseUrl}/api/usuarios/${formData._id}` : `${baseUrl}/api/usuarios`;
    const method = isEditing ? 'put' : 'post';

    // Se for edição e senha não foi alterada, removemos do objeto
    if (isEditing && !formData.senha) delete formData.senha;

    try {
      const response = await axios[method](url, formData, config);
      
      // Se o admin editou seu próprio perfil e alterou a API Key ou OrgID, atualizar o localStorage
      if (isEditing) {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const updatedUser = response.data.user;
        
        // Verificar se é o próprio usuário logado
        if (userData.id === formData._id) {
          // Atualizar tokenClient se a API Key foi alterada
          if (updatedUser.vapiApiKey) {
            localStorage.setItem('tokenClient', updatedUser.vapiApiKey);
            console.log('API Key atualizada no localStorage');
          } else {
            // Se a API Key foi removida, limpar o tokenClient
            localStorage.removeItem('tokenClient');
            console.log('API Key removida do localStorage');
          }

          // Atualizar orgID se foi alterado
          if (updatedUser.orgID) {
            localStorage.setItem('orgID', updatedUser.orgID);
            console.log('OrgID atualizado no localStorage');
          } else {
            // Se o OrgID foi removido, limpar do localStorage
            localStorage.removeItem('orgID');
            console.log('OrgID removido do localStorage');
          }
        }
      }
      
      toast.success(isEditing ? 'Usuário atualizado!' : 'Usuário criado!', { id: toastId });
      setIsModalOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ocorreu um erro.', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar usuário
  const handleDeleteUser = async (user) => {
    if (window.confirm(`Tem certeza que deseja remover "${user.nome || user.name || 'Sem nome'}"?`)) {
      const config = getAuthConfig();
      if (!config) return;
      const toastId = toast.loading('Excluindo...');
      try {
        await axios.delete(`${baseUrl}/api/usuarios/${user._id}`, config);
        toast.success('Usuário removido!', { id: toastId });
        fetchUsers();
      } catch (err) {
        toast.error('Erro ao remover.', { id: toastId });
      }
    }
  };

  // Função para atualizar créditos
  const handleUpdateCredits = async ({ userId, quantidade, descricao }) => {
    setIsSubmitting(true);
    const config = getAuthConfig();
    if (!config) {
      setIsSubmitting(false);
      return;
    }
    const toastId = toast.loading(quantidade > 0 ? 'Adicionando créditos...' : 'Removendo créditos...');
    const endpoint = quantidade > 0 ? 'adicionar-creditos' : 'consumir-creditos';

    try {
      const url = `${baseUrl}/api/usuarios/${userId}/${endpoint}`;
      await axios.post(url, {
        quantidade: Math.abs(quantidade),
        descricao
      }, config);
      toast.success(quantidade > 0 ? 'Créditos adicionados com sucesso!' : 'Créditos removidos com sucesso!', { id: toastId });
      setIsCreditsModalOpen(false);
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Ocorreu um erro ao atualizar os créditos';
      toast.error(errorMessage, { id: toastId });
      console.error('Erro ao atualizar créditos:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para bloquear/desbloquear usuário
  const handleBlockUser = async (user) => {
    // Verificar se o usuário atual tem permissão para bloquear
    if (!isAdminOrSuper) {
      toast.error('Apenas administradores podem bloquear/desbloquear usuários.');
      return;
    }

    const newStatus = !user.creditos?.bloqueado;
    const actionText = newStatus ? 'bloquear' : 'desbloquear';

    if (window.confirm(`Tem certeza que deseja ${actionText} o usuário "${user.nome || user.name || 'Sem nome'}"?`)) {
      const config = getAuthConfig();
      if (!config) return;
      const toastId = toast.loading(`${actionText.charAt(0).toUpperCase() + actionText.slice(1)}ando...`);

      try {
        const url = `${baseUrl}/api/usuarios/${user._id}/bloquear`;
        await axios.put(url, {
          bloqueado: newStatus
        }, config);

        toast.success(`Usuário ${actionText}ado com sucesso!`, { id: toastId });
        fetchUsers();
      } catch (err) {
        toast.error(`Erro ao ${actionText}ar usuário.`, { id: toastId });
        console.error(`Erro ao ${actionText}ar usuário:`, err);
      }
    }
  };

  // Componente para exibir o status de bloqueio
  const BlockStatus = ({ bloqueado }) => (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1 text-xs font-medium",
        bloqueado ? "bg-red-600 text-white" : "bg-[#14532d] text-white"
      )}
    >
      {bloqueado ? <LockClosedIcon className="h-3 w-3" /> : <LockOpenIcon className="h-3 w-3" />}
      {bloqueado ? 'BLOQUEADO' : 'ATIVO'}
    </Badge>
  );

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <UserForm
          user={editingUser}
          onSave={handleSaveUser}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          currentUserRole={currentUserRole}
          assistentes={assistentes}
          phoneNumbers={phoneNumbers}
        />
      </Modal>
      <Modal isOpen={isCreditsModalOpen} onClose={() => setIsCreditsModalOpen(false)}>
        {selectedUserForCredits && (
          <CreditsModal
            user={selectedUserForCredits}
            onSave={handleUpdateCredits}
            onCancel={() => setIsCreditsModalOpen(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </Modal>
      <Modal isOpen={isLogsModalOpen} onClose={() => setIsLogsModalOpen(false)}>
        {selectedUserForLogs && (
          <CreditLogsModal
            user={selectedUserForLogs}
            logs={creditLogs}
            loading={loadingCreditLogs}
            onClose={() => setIsLogsModalOpen(false)}
          />
        )}
      </Modal>


      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Gestão de Usuários</h1>
            <p className="text-sm text-muted-foreground">Crie, edite e gerencie permissões e créditos.</p>
          </div>
          {isAdminOrSuper && (
            <Button onClick={() => { setEditingUser(null); setIsModalOpen(true); }}>
              <UserPlusIcon className="h-5 w-5" />
              Criar Usuário
            </Button>
          )}
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="p-4 font-semibold">Nome</th>
                <th className="p-4 font-semibold">Email</th>
                <th className="p-4 font-semibold">Tipo</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold text-center">Créditos</th>
                <th className="p-4 font-semibold text-center">Limite Assistentes</th>
                <th className="p-4 font-semibold text-center">Taxa ($)</th>
                <th className="p-4 font-semibold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center p-8 text-muted-foreground">Carregando usuários...</td></tr>
              ) : error ? (
                <tr><td colSpan="8" className="text-center p-8 text-destructive">{error}</td></tr>
              ) : (
                usuarios.map((usuario) => (
                  <tr key={usuario._id} className="border-b border-border hover:bg-muted/40 transition-colors">
                    <td className="p-4">{usuario.nome || usuario.name || 'Sem nome'}</td>
                    <td className="p-4">{usuario.email}</td>
                    <td className="p-4">
                      <Badge
                        variant={usuario.role === 'admin' ? "secondary" : "outline"}
                        className={usuario.role === 'admin' ? "bg-primary/10 text-primary border-primary/20" : ""}
                      >
                        {usuario.role}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <BlockStatus bloqueado={usuario.creditos?.bloqueado} />
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className={`${usuario.creditos?.saldo < 0 ? 'text-destructive' : usuario.creditos?.saldo === 0 ? 'text-yellow-500' : 'text-emerald-600'} font-mono font-semibold`}>
                          {usuario.creditos?.saldo?.toFixed(2) || '0.00'}
                        </span>
                        {usuario.creditos?.bloqueado && (
                          <Badge variant="destructive" className="mt-1 text-[10px]">
                            Bloqueado
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center">{usuario.limiteAssistentes}</td>
                    <td className="p-4 text-center font-mono text-sm">{usuario.taxa?.toFixed(2) || '0.00'}</td>
                    <td className="p-4 text-center flex justify-center items-center gap-2">
                      {isAdminOrSuper && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setEditingUser(usuario); setIsModalOpen(true); }}
                            title="Editar"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </Button>
                          {!(
                            currentUserRole === 'admin' &&
                            (
                              usuario._id === storedUserData.id ||
                              usuario.role === 'admin' ||
                              usuario.role === 'super_admin'
                            )
                          ) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setSelectedUserForCredits(usuario); setIsCreditsModalOpen(true); }}
                              title="Gerenciar Créditos"
                            >
                              <CurrencyDollarIcon className="h-5 w-5" />
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenCreditLogs(usuario)}
                              title="Ver histórico de créditos"
                            >
                              <DocumentTextIcon className="h-5 w-5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleBlockUser(usuario)}
                            className={usuario.creditos?.bloqueado ? 'text-emerald-600' : 'text-destructive'}
                            title={usuario.creditos?.bloqueado ? "Desbloquear" : "Bloquear"}
                          >
                            {usuario.creditos?.bloqueado ? (
                              <LockOpenIcon className="h-5 w-5" />
                            ) : (
                              <LockClosedIcon className="h-5 w-5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(usuario)}
                            title="Remover"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
